import React, { useEffect, useState } from "react";
import { useStep } from "../../../context/stepContext";
import { ChairModel, useChair } from "../../../context/ChairContext";
import { Step } from "./Step";
import { ChairInfoButton } from "../ChairInfoButton";
import { useGetChairConfiguratorInfo, useListVariantCovers } from "../../../api/hooks/useChairs";
import { isEqual, isNull } from "lodash";

type ChairOptionProps = {
    selected: boolean;
    title: string;
    subtitle: string;
    value: string;
    available: boolean;
    buttonDisabled: boolean;
    onClick: () => void;
    buttonText?: string;
};

const ChairOption = (props: ChairOptionProps) => {
    return (
        <div className={`chair-option ${props.available ? "available" : "disabled"}`}>
            <div>
                <div className={`title ${props.selected ? "selected" : "unselected"}`}>{props.title}</div>
                <div className={"subtitle"}>{props.subtitle}</div>
                <div className={"value"}>{props.value}</div>
            </div>
            {props.available ? (
                <button
                    className={`button ${
                        props.selected ? "primary selected" : "secondary unselected"
                    } icon-button option-button`}
                    disabled={props.buttonDisabled}
                    onClick={props.onClick}
                >
                    {props.selected ? "Bearbeiten" : "Wählen"}
                </button>
            ) : null}
        </div>
    );
};

export const OverviewStep = () => {
    const { setStep } = useStep();
    const { chair, setChair } = useChair();
    const [hasNoCover, setHasNoCover] = useState<boolean>(false);

    const variantCoversQuery = useListVariantCovers({ id: chair.id });
    const variantCovers = variantCoversQuery.isSuccess
        ? (variantCoversQuery.data.chairEntries[0].variantCost ?? [])
        : [];

    const chairConfigInfoQuery = useGetChairConfiguratorInfo({ id: chair.id });
    const accessories = chairConfigInfoQuery.isSuccess
        ? (chairConfigInfoQuery.data.chairEntries[0].parent.accessory ?? null)
        : null;

    useEffect(() => {
        if (variantCoversQuery.isSuccess)
            setHasNoCover(
                variantCovers?.length === 1 &&
                    variantCovers[0].cover.length === 1 &&
                    variantCovers[0].cover[0].withoutCover,
            );
    }, [variantCoversQuery.isSuccess, setHasNoCover]);

    useEffect(() => {
        const chairUpdate: ChairModel = { ...chair };

        if (hasNoCover && variantCovers?.length === 1 && variantCovers[0].cover.length === 1) {
            const cover = variantCovers[0].cover[0];
            chairUpdate.cover = {
                className: cover.title,
                price: variantCovers[0].coverCost,
                catalog: { name: "", id: "", shininess: 0 },
                color: { url: "", name: "", id: "" },
            };
        }
        if (chairConfigInfoQuery.isSuccess && (isNull(accessories) || accessories.length === 0)) {
            chairUpdate.extra = {
                articleID: "",
                name: "Kein Zubehör",
                price: 0,
            };
        }
        if (!isEqual(chair, chairUpdate)) {
            setChair(chairUpdate);
        }
    }, [hasNoCover, variantCovers, accessories, chairConfigInfoQuery.isSuccess, chair]);

    return (
        <div className={"step overview-step"}>
            <h2 className={"heading"}>Schritt 2: Ihre Konfiguration</h2>
            <div className={"mobile-buttons"}>
                <ChairInfoButton />
            </div>
            {chair ? (
                <div className={"chair-options"}>
                    <ChairOption
                        selected={!!chair.modelName}
                        title={"Modell"}
                        subtitle={chair.modelName ?? ""}
                        value={""}
                        available={true}
                        buttonDisabled={false}
                        onClick={() => {
                            setStep(Step.CATALOG);
                        }}
                    />
                    <ChairOption
                        selected={!!chair.variant}
                        title={"Variante"}
                        subtitle={chair.variant ?? "Bitte wählen Sie eine Variante"}
                        value={chair.variantDescription ?? ""}
                        available={true}
                        buttonDisabled={!!!chair.modelName}
                        onClick={() => {
                            setStep(Step.VARIANT);
                        }}
                    />
                    <ChairOption
                        selected={!!chair.frame && !!chair.frame.texture}
                        title={"Gestell"}
                        subtitle={chair.frame?.name ?? "Bitte wählen Sie ein Material"}
                        value={chair.frame?.texture?.name ?? "Bitte wählen Sie eine Verarbeitung aus"}
                        available={true}
                        buttonDisabled={!!!chair.variant}
                        onClick={() => {
                            setStep(Step.FRAME);
                        }}
                    />
                    <ChairOption
                        selected={!!chair.cover?.className && !!chair.cover?.catalog && !!chair.cover?.color}
                        title={"Bezug"}
                        subtitle={chair.cover?.className ?? "Bitte wählen Sie einen Bezug"}
                        value={`${chair.cover?.catalog?.name ?? ""} ${chair.cover?.color?.name ?? ""}`}
                        available={!hasNoCover}
                        buttonDisabled={!!!chair.variant}
                        onClick={() => {
                            setStep(Step.COVER);
                        }}
                    />
                    <ChairOption
                        selected={!!chair.extra}
                        title={"Zubehör"}
                        subtitle={chair.extra?.name ?? "Bitte wählen Sie Ihr Zubehör"}
                        value={""}
                        available={!(isNull(accessories) || accessories.length === 0)}
                        buttonDisabled={false}
                        onClick={() => {
                            setStep(Step.ACCESSORY);
                        }}
                    />
                </div>
            ) : null}
        </div>
    );
};
